import { URLs } from "@/constants";
import { Google, IApp, type IApiRequest } from "@/types";
import { apiURL, flatten, sendRequest, sendRequestWithAnalyzeAllPolling } from "@/utils";

namespace GoogleAnalytics {
  export async function getAnalyticsData(
    userId: string,
    customerId: string,
    accountId: string,
    payload: IApiRequest.GoogleAnalyticsTotalInput
  ): Promise<IApp.IApiResponse<{ data: IApiRequest.GoogleAnalyticsTotalOutput }>>;

  export async function getAnalyticsData(
    userId: string,
    customerId: string,
    accountId: string,
    payload: IApiRequest.GoogleAnalyticsPageViewsInput & IApiRequest.Pagination
  ): Promise<IApp.IApiResponse<{ data: IApiRequest.GoogleAnalyticsPageViewsOutput }>>;

  export async function getAnalyticsData(
    userId: string,
    customerId: string,
    accountId: string,
    payload: IApiRequest.GoogleAnalyticsSessionCountInput & IApiRequest.Pagination
  ): Promise<IApp.IApiResponse<{ data: IApiRequest.GoogleAnalyticsSessionCountOutput }>>;

  export async function getAnalyticsData(
    userId: string,
    customerId: string,
    accountId: string,
    payload: IApiRequest.GoogleAnalyticsEngagedSessionsInput
  ): Promise<IApp.IApiResponse<{ data: IApiRequest.GoogleAnalyticsEngagedSessionsOutput }>>;

  export async function getAnalyticsData(
    userId: string,
    customerId: string,
    accountId: string,
    payload: IApiRequest.GoogleAnalyticsCityActiveUsersMonthlyInput & IApiRequest.Pagination
  ): Promise<IApp.IApiResponse<{ data: IApiRequest.GoogleAnalyticsCityActiveUsersMonthlyOutput }>>;

  export async function getAnalyticsData(
    userId: string,
    customerId: string,
    accountId: string,
    payload: IApiRequest.GoogleAnalyticsEventCountInput & IApiRequest.Pagination
  ): Promise<IApp.IApiResponse<{ data: IApiRequest.GoogleAnalyticsEventCountOutput }>>;

  export async function getAnalyticsData(
    userId: string,
    customerId: string,
    accountId: string,
    payload: IApiRequest.GoogleAnalyticsMonthlyEventCountInput & IApiRequest.Pagination
  ): Promise<IApp.IApiResponse<{ data: IApiRequest.GoogleAnalyticsMonthlyEventCountOutput }>>;

  export async function getAnalyticsData(
    userId: string,
    customerId: string,
    accountId: string,
    payload: IApiRequest.GoogleAnalyticsMonthlyDeviceActiveUsersInput
  ): Promise<
    IApp.IApiResponse<{ data: IApiRequest.GoogleAnalyticsMonthlyDeviceActiveUsersOutput }>
  >;

  export async function getAnalyticsData(
    userId: string,
    customerId: string,
    accountId: string,
    payload: IApiRequest.GoogleAnalyticsOrganicTotalInput
  ): Promise<IApp.IApiResponse<{ data: IApiRequest.GoogleAnalyticsOrganicTotalMonthlyOutput }>>;

  export async function getAnalyticsData(
    userId: string,
    customerId: string,
    accountId: string,
    payload: IApiRequest.GoogleAnalyticsOrganicCityActiveUsersMonthlyInput & IApiRequest.Pagination
  ): Promise<IApp.IApiResponse<{ data: IApiRequest.GoogleAnalyticsCityActiveUsersMonthlyOutput }>>;

  export async function getAnalyticsData(
    userId: string,
    customerId: string,
    accountId: string,
    payload: IApiRequest.GoogleAnalyticsMonthlyOrganicDeviceActiveUsersInput
  ): Promise<
    IApp.IApiResponse<{ data: IApiRequest.GoogleAnalyticsMonthlyDeviceActiveUsersOutput }>
  >;

  export async function getAnalyticsData(
    userId: string,
    customerId: string,
    accountId: string,
    payload:
      | IApiRequest.GoogleAnalyticsTotalInput
      | (IApiRequest.GoogleAnalyticsPageViewsInput & IApiRequest.Pagination)
      | (IApiRequest.GoogleAnalyticsSessionCountInput & IApiRequest.Pagination)
      | IApiRequest.GoogleAnalyticsEngagedSessionsInput
      | (IApiRequest.GoogleAnalyticsCityActiveUsersMonthlyInput & IApiRequest.Pagination)
      | (IApiRequest.GoogleAnalyticsEventCountInput & IApiRequest.Pagination)
      | (IApiRequest.GoogleAnalyticsMonthlyEventCountInput & IApiRequest.Pagination)
      | IApiRequest.GoogleAnalyticsMonthlyDeviceActiveUsersInput
      | IApiRequest.GoogleAnalyticsOrganicTotalInput
      | (IApiRequest.GoogleAnalyticsOrganicCityActiveUsersMonthlyInput & IApiRequest.Pagination)
      | IApiRequest.GoogleAnalyticsMonthlyOrganicDeviceActiveUsersInput
  ): Promise<
    | IApp.IApiResponse<{
        data: IApiRequest.GoogleAnalyticsTotalOutput;
      }>
    | IApp.IApiResponse<{
        data: IApiRequest.GoogleAnalyticsPageViewsOutput;
      }>
    | IApp.IApiResponse<{
        data: IApiRequest.GoogleAnalyticsSessionCountOutput;
      }>
    | IApp.IApiResponse<{
        data: IApiRequest.GoogleAnalyticsEngagedSessionsOutput;
      }>
    | IApp.IApiResponse<{
        data: IApiRequest.GoogleAnalyticsCityActiveUsersMonthlyOutput;
      }>
    | IApp.IApiResponse<{
        data: IApiRequest.GoogleAnalyticsEventCountOutput;
      }>
    | IApp.IApiResponse<{
        data: IApiRequest.GoogleAnalyticsMonthlyEventCountOutput;
      }>
    | IApp.IApiResponse<{
        data: IApiRequest.GoogleAnalyticsMonthlyDeviceActiveUsersOutput;
      }>
    | IApp.IApiResponse<{
        data: IApiRequest.GoogleAnalyticsOrganicTotalMonthlyOutput;
      }>
  > {
    return await sendRequest<{ data: any }>({
      url: apiURL(URLs.API_GOOGLE_ANALYTICS_ANALYTICS(userId, customerId, accountId)),
      method: "GET",
      params: flatten(payload)
    });
  }
  export const getAllAnalyticsData = async (
    userId: string,
    customerId: string,
    accountId: string,
    payload: {
      monthRange: { start: string; end: string };
      comparedMonthRange: { start: string; end: string };
    }
  ) => {
    return await sendRequestWithAnalyzeAllPolling<{
      total: IApiRequest.GoogleAnalyticsTotalOutput | null;
      "total-compared": IApiRequest.GoogleAnalyticsTotalOutput | null;
      "page-views": IApiRequest.GoogleAnalyticsPageViewsOutput | null;
      "session-count": IApiRequest.GoogleAnalyticsSessionCountOutput | null;
      "monthly-city-active-users": IApiRequest.GoogleAnalyticsCityActiveUsersMonthlyOutput | null;
      "event-count": IApiRequest.GoogleAnalyticsEventCountOutput | null;
      "monthly-event-count": IApiRequest.GoogleAnalyticsMonthlyEventCountOutput | null;
      "monthly-device-active-users": IApiRequest.GoogleAnalyticsMonthlyDeviceActiveUsersOutput | null;
      "monthly-device-active-users-compared": IApiRequest.GoogleAnalyticsMonthlyDeviceActiveUsersOutput | null;
      "organic-total": IApiRequest.GoogleAnalyticsOrganicTotalMonthlyOutput | null;
      "organic-total-compared": IApiRequest.GoogleAnalyticsOrganicTotalMonthlyOutput | null;
      "organic-city-active-users": IApiRequest.GoogleAnalyticsCityActiveUsersMonthlyOutput | null;
      "organic-device-active-users": IApiRequest.GoogleAnalyticsMonthlyDeviceActiveUsersOutput | null;
      "organic-device-active-users-compared": IApiRequest.GoogleAnalyticsMonthlyDeviceActiveUsersOutput | null;
    }>({
      url: apiURL(URLs.API_GOOGLE_ANALYTICS_ANALYZE_ALL(userId, customerId, accountId)),
      method: "GET",
      params: flatten(payload)
    });
  };

  export async function getAccountList(userId: string, customerId: string, accountId: string) {
    return await sendRequest<{ accounts: IApp.IGoogleAnalyticsAdminAccount[] }>({
      url: apiURL(URLs.API_GOOGLE_ANALYTICS__ACCOUNT_ACCOUNT_LIST(userId, customerId, accountId)),
      method: "GET"
    });
  }
  export async function getAccountPropertyList(
    userId: string,
    customerId: string,
    accountId: string,
    accountAccountId: string
  ) {
    return await sendRequest<{ properties: Google.GoogleAnalyticsAdminAccountProperty[] }>({
      url: apiURL(
        URLs.API_GOOGLE_ANALYTICS__ACCOUNT_ACCOUNT_PROPERTY_LIST(userId, customerId, accountId)
      ),
      method: "GET",
      params: {
        accountId: accountAccountId
      }
    });
  }
}

export default GoogleAnalytics;
